/**
 * Phase 2 (Feb 2026) — Control-actions bar on Home.
 *
 * Three visible handbrake buttons wired to /api/v1/policy-guard/*:
 *   • Auftrag abbrechen  → POST /policy-guard/orchestration/cancel/{run_id}
 *   • Automatik pausieren → POST /policy-guard/autopilot/pause
 *   • Werbung stoppen    → POST /policy-guard/campaigns/{id}/stop-delivery
 *
 * Buttons always report the differentiated backend state (requested /
 * provider_confirmed / partial / unclear) so the user never sees a fake
 * "success" toast. Selection modals let the user pick target run/campaign
 * when there is more than one candidate.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import { Ban, PauseCircle, StopCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

const STATE_LABELS = {
  requested: {
    text: "Stopp angefordert. Neue Aktionen gesperrt. Auslieferung wird angehalten und geprüft.",
    tone: "outline",
  },
  provider_confirmed: { text: "Vom Anbieter geprüft — Werbung ist gestoppt", tone: "default" },
  confirmed_stopped: { text: "Vom Anbieter geprüft — Werbung ist gestoppt", tone: "default" },
  partial: { text: "Teilweise erledigt — pro Plattform ansehen", tone: "secondary" },
  unclear: { text: "Beim Anbieter noch ungeklärt — wird nachverfolgt", tone: "secondary" },
  already_paused: { text: "Bereits pausiert", tone: "outline" },
  already_terminal: { text: "Bereits beendet", tone: "outline" },
  not_delivering: { text: "Kampagne liefert nicht", tone: "outline" },
};

function StateBadge({ state, testid }) {
  const meta = STATE_LABELS[state] || { text: state, tone: "outline" };
  return (
    <Badge variant={meta.tone} data-testid={testid}>
      {meta.text}
    </Badge>
  );
}

export default function ControlActions() {
  const [runs, setRuns] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [autopilotMode, setAutopilotMode] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(null); // 'cancel' | 'stop'
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api
      .get("/orchestrator/runs?limit=10&status=running")
      .then((r) => setRuns(r.data?.items || []))
      .catch(() => setRuns([]));
    api
      .get("/campaigns?limit=20")
      .then((r) =>
        setCampaigns(
          (r.data?.items || []).filter((c) =>
            ["published", "scheduled", "paused"].includes(c.publish_status),
          ),
        ),
      )
      .catch(() => setCampaigns([]));
    api
      .get("/autopilot/policy")
      .then((r) => setAutopilotMode(r.data?.mode || "off"))
      .catch(() => setAutopilotMode("off"));
  }, []);

  async function cancelRun(runId) {
    setBusy("cancel");
    try {
      const r = await api.post(
        `/policy-guard/orchestration/cancel/${runId}`,
        { reason: "user_home_action" },
      );
      setLastResult({ kind: "cancel", ...r.data });
      toast.success(`Auftrag abgebrochen (${r.data?.cancelled_jobs || 0} Jobs)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Abbruch fehlgeschlagen");
    } finally {
      setBusy(null);
      setPickerOpen(null);
    }
  }

  async function pauseAutopilot() {
    setBusy("pause");
    try {
      const r = await api.post(`/policy-guard/autopilot/pause`, {
        reason: "user_home_action",
      });
      setLastResult({ kind: "pause", ...r.data });
      setAutopilotMode("off");
      toast.success(
        `Automatik pausiert · ${r.data?.revoked_delegations_count || 0} Delegationen widerrufen`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Pause fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  }

  // FIX 4-UI (Feb 2026, Korrektur 3) — stopDelivery is ALWAYS two-phase.
  // Phase A: "Stopp angefordert. Neue Aktionen gesperrt. Auslieferung wird
  //          angehalten und geprüft." — shown immediately, never contains
  //          "gestoppt", "garantiert", "bestätigt".
  // Phase B: only after (a) the backend reports a definitive external state
  //          AND (b) a short client-side verification window (2.5s) elapsed
  //          — even in the mock case where the backend returns
  //          provider_confirmed synchronously. Partial / unclear results
  //          keep the per-platform breakdown and do NOT smooth to a
  //          global "stopped".
  const STOP_VERIFY_MS = 2500;

  async function stopDelivery(campaignId) {
    setBusy("stop");
    setPickerOpen(null);
    // Phase A — render "requested" immediately, regardless of the
    // backend response speed.
    setLastResult({
      kind: "stop",
      state: "requested",
      phase: "A",
      campaign_id: campaignId,
      per_platform: null,
    });
    let backendResult = null;
    try {
      const r = await api.post(
        `/policy-guard/campaigns/${campaignId}/stop-delivery`,
        { reason: "user_home_action" },
      );
      backendResult = r.data || {};
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Stopp fehlgeschlagen");
      setLastResult((prev) => ({
        ...(prev || { kind: "stop" }),
        state: "unclear",
        phase: "B",
        error: e?.response?.data?.detail?.error || "network_error",
      }));
      setBusy(null);
      return;
    }
    // Phase B — reveal the definitive state after a short client-side
    // verification window. Toast messaging strictly avoids "gestoppt"
    // until here.
    setTimeout(() => {
      setLastResult({
        kind: "stop",
        phase: "B",
        campaign_id: campaignId,
        state: backendResult.state,
        per_platform: backendResult.per_platform || null,
      });
      const finalState = backendResult.state;
      if (finalState === "provider_confirmed" || finalState === "confirmed_stopped") {
        toast.success("Werbung ist beim Anbieter geprüft angehalten.");
      } else if (finalState === "partial") {
        toast.warning("Teilweise erledigt — bitte Plattform-Details prüfen.");
      } else if (finalState === "unclear") {
        toast.warning("Beim Anbieter ungeklärt — Reconciler folgt nach.");
      } else {
        toast(STATE_LABELS[finalState]?.text || finalState);
      }
      setBusy(null);
    }, STOP_VERIFY_MS);
    // Immediate Phase-A toast — deliberate wording, no forbidden terms.
    toast("Stopp angefordert. Neue Aktionen gesperrt. Auslieferung wird angehalten und geprüft.");
  }

  return (
    <Card data-testid="control-actions" className="border-destructive/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Handbremsen</CardTitle>
        <p className="text-xs text-muted-foreground">
          Drei Aktionen, sofort erreichbar. Rückmeldung zeigt den echten
          Anbieter-Status — keine geschönten Erfolgsmeldungen.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="justify-start"
            data-testid="control-cancel-run"
            disabled={runs.length === 0 || busy === "cancel"}
            onClick={() => (runs.length === 1 ? cancelRun(runs[0].id) : setPickerOpen("cancel"))}
          >
            {busy === "cancel" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Ban className="w-4 h-4 mr-2" />
            )}
            Auftrag abbrechen
            <span className="ml-auto text-[10px] text-muted-foreground">
              {runs.length} offen
            </span>
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            data-testid="control-pause-autopilot"
            disabled={autopilotMode === "off" || autopilotMode === null || busy === "pause"}
            onClick={pauseAutopilot}
          >
            {busy === "pause" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PauseCircle className="w-4 h-4 mr-2" />
            )}
            Automatik pausieren
            <span className="ml-auto text-[10px] text-muted-foreground">
              {autopilotMode || "…"}
            </span>
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            data-testid="control-stop-delivery"
            disabled={campaigns.length === 0 || busy === "stop"}
            onClick={() =>
              campaigns.length === 1
                ? stopDelivery(campaigns[0].id)
                : setPickerOpen("stop")
            }
          >
            {busy === "stop" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <StopCircle className="w-4 h-4 mr-2" />
            )}
            Werbung stoppen
            <span className="ml-auto text-[10px] text-muted-foreground">
              {campaigns.length} aktiv
            </span>
          </Button>
        </div>

        {lastResult && (
          <Alert
            data-testid="control-actions-last-result"
            data-phase={lastResult.phase || "B"}
            data-state={lastResult.state}
          >
            <AlertTitle className="text-sm">
              Letzte Aktion — {lastResult.kind}
              {lastResult.kind === "stop" && lastResult.phase === "A" && (
                <span
                  className="ml-2 text-xs font-normal text-muted-foreground"
                  data-testid="control-stop-verifying"
                >
                  · wird geprüft …
                </span>
              )}
            </AlertTitle>
            <AlertDescription className="text-xs space-y-1">
              <div className="flex items-center gap-2">
                Status: <StateBadge state={lastResult.state} testid="control-last-state" />
              </div>
              {lastResult.per_platform && (
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {Object.entries(lastResult.per_platform).map(([p, s]) => (
                    <div key={p} className="flex items-center justify-between">
                      <span className="uppercase text-[10px] text-muted-foreground">{p}</span>
                      <StateBadge state={s} testid={`control-per-platform-${p}`} />
                    </div>
                  ))}
                </div>
              )}
              {lastResult.revoked_delegations_count != null && (
                <div>
                  Widerrufene Delegationen:{" "}
                  <span
                    className="font-mono"
                    data-testid="control-revoked-delegations-count"
                  >
                    {lastResult.revoked_delegations_count}
                  </span>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>

      {/* Cancel-run picker */}
      <Dialog open={pickerOpen === "cancel"} onOpenChange={(v) => !v && setPickerOpen(null)}>
        <DialogContent data-testid="control-cancel-picker">
          <DialogHeader>
            <DialogTitle>Welchen Auftrag abbrechen?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {runs.map((r) => (
              <button
                key={r.id}
                onClick={() => cancelRun(r.id)}
                data-testid={`control-cancel-pick-${r.id}`}
                className="w-full text-left rounded border px-3 py-2 text-sm hover:bg-muted"
              >
                <div className="font-mono text-xs text-muted-foreground">
                  {(r.id || "").slice(0, 8)}
                </div>
                <div>{r.brief_id ? `Brief ${r.brief_id.slice(0, 8)}` : "Run"}</div>
              </button>
            ))}
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>

      {/* Stop-delivery picker */}
      <Dialog open={pickerOpen === "stop"} onOpenChange={(v) => !v && setPickerOpen(null)}>
        <DialogContent data-testid="control-stop-picker">
          <DialogHeader>
            <DialogTitle>Welche Kampagne stoppen?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={() => stopDelivery(c.id)}
                data-testid={`control-stop-pick-${c.id}`}
                className="w-full text-left rounded border px-3 py-2 text-sm hover:bg-muted"
              >
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">
                  {c.publish_status} · {(c.platforms || []).join(", ")}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
